import Guid from "common/values/guid/guid";
import DocumentName from "documents/values/document-name";
import DocumentTopic, { DocumentTopicJSON } from "documents/values/document-topic";
import {DocumentType} from "documents/values/document-type";
import moment, {Moment} from "moment";

export interface DocumentJSON {
  id: string;
  name?: string;
  creatorId?: string | null;
  fileSize: number;
  fileType: string;
  created?: string;
  readAccess?: string[];
  writeAccess?: string[];
  topics: DocumentTopicJSON[];
  templateIds: string[];
  hasSystemPermissions: boolean;
}

export default class Document {
  id: Guid;
  name: DocumentName | undefined;
  creatorId: Guid | null;
  fileSize: number;
  fileType: DocumentType;
  created: Moment | undefined;
  readAccess?: Guid[];
  writeAccess?: Guid[];
  topics: DocumentTopic[];
  templateIds: Guid[] = [];
  hasSystemPermissions: boolean = false;

  constructor(
    id: Guid,
    name: DocumentName | undefined,
    creatorId: Guid | null,
    fileSize: number,
    fileType: DocumentType,
    created: Moment | undefined,
    readAccess: Guid[] | undefined,
    writeAccess: Guid[] | undefined,
    topics: DocumentTopic[],
    templateIds: Guid[],
    hasSystemPermissions: boolean
  ) {

    this.id = id;
    this.name = name;
    this.creatorId = creatorId;
    this.fileSize = fileSize;
    this.fileType = fileType;
    this.created = created;
    this.readAccess = readAccess;
    this.writeAccess = writeAccess;
    this.topics = topics;
    this.templateIds = templateIds;
    this.hasSystemPermissions = hasSystemPermissions;
  }

  public fromJSON(json: DocumentJSON): Document {
    return Document.fromJSON(json);
  }

  public static fromJSON(json: DocumentJSON): Document {
    if (!json) throw new Error("Invalid JSON");

    const id = Guid.fromJSON(json.id);
    const creatorId = json.creatorId ? Guid.fromJSON(json.creatorId) : null;
    const name = json.name ? DocumentName.fromJSON(json.name) : undefined;
    const fileSize = json.fileSize;
    const fileType = json.fileType as DocumentType;
    const created = json.created ? moment(json.created) : undefined;
    const readAccess = json.readAccess?.map((id) => Guid.fromJSON(id));
    const writeAccess = json.writeAccess?.map((id) => Guid.fromJSON(id));
    const topics = json.topics.map((topic) => DocumentTopic.fromJSON(topic));
    const templateIds = json.templateIds.map((id) => Guid.fromJSON(id));
    const hasSystemPermissions = Boolean(json.hasSystemPermissions);

    if (!id) throw new Error("Document Id is required");

    return new Document(
      id,
      name,
      creatorId,
      fileSize,
      fileType,
      created,
      readAccess,
      writeAccess,
      topics,
      templateIds,
      hasSystemPermissions
    );
  }

  public toJSON(): DocumentJSON {
    return {
      id: this.id.toJSON(),
      name: this.name?.toJSON(),
      creatorId: this.creatorId?.toJSON(),
      fileSize: this.fileSize,
      fileType: this.fileType,
      created: this.created?.toISOString(),
      readAccess: this.readAccess?.map((id) => id.toJSON()),
      writeAccess: this.writeAccess?.map((id) => id.toJSON()),
      topics: this.topics.map((topic) => topic.toJSON()),
      templateIds: this.templateIds.map((id) => id.toJSON()),
      hasSystemPermissions: this.hasSystemPermissions
    };
  }

  public get isTemplate(): boolean {
    return this.topics.some((topic) => topic.context?.toLowerCase().includes("template"));
  }

  public static get Prototype(): Document {
    return new Document(
      Guid.generate(),
      undefined,
      Guid.generate(),
      0,
      DocumentType.UNKNOWN,
      moment(),
      [],
      [],
      [],
      [],
      false
    );
  }

  public isUsingTemplate(templateId: Guid): boolean {
    return this.templateIds.some((id) => id.isEqualTo(templateId));
  }

  public clone(): Document {
    return new Document(
      this.id,
      this.name,
      this.creatorId,
      this.fileSize,
      this.fileType,
      this.created,
      this.readAccess,
      this.writeAccess,
      this.topics,
      this.templateIds,
      this.hasSystemPermissions
    );
  }

  public get isEmpty(): boolean {
    return false;
  }

  public isEqualTo(document: Document | null): boolean {
    return Boolean(document && this.id.isEqualTo(document.id));
  }
}
